/**
 * StorageArraysList — Cabinas de almacenamiento (estándar de listados del CMDB).
 *
 * El andamiaje lo pone `common/ListPage.jsx`: buscador, filtros, exportar a
 * Excel, columnas, cebra, estados y los botones `[acciones propias] · Copiar ·
 * Editar · Eliminar`. Clic en la fila abre la ficha EN LECTURA.
 *
 * Lo propio de esta pantalla:
 *  · Vistas: **Lista** (por defecto) y **Fichas**.
 *  · Filtros: Tipo, Estado, Entorno, Protocolo, Ubicación, Rack y Capacidad usable.
 *  · Acciones propias: **Ver rack**, **Discos** (inventario de bahías),
 *    **Componentes** (NICs / HBAs) y **Mapa de puertos** (el cableado de esos
 *    puertos, con el mismo modal que usan paneles y switches).
 *  · La copia reutiliza el nombre que sugiere la nomenclatura y clona también
 *    componentes, puertos e inventario de discos (sin número de serie ni bahía).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CircleStackIcon,
  CubeIcon,
  CpuChipIcon,
  MapIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import Dialog from './common/Dialog';
import StorageArrayForm from './StorageArrayForm';
import ServerForm from './ServerForm';
import ComponentsModal from './ComponentsModal';
import PortMapModal from './PortMapModal';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import useLocationPaths from '../hooks/useLocationPaths';
import { getServerFormStyle } from '../utils/serverFormStyle';
import api from '../services/api';

const FLAVOR_LABELS = {
  appliance: 'Cabina dedicada',
  server_based: 'Servidor + Software',
};
const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
  decom:       { label: 'Decom',         badge: 'list-badge--off' },
};
/** Rangos del filtro «Capacidad usable», en TB. */
const CAPACITY_RANGES = {
  small:  { label: '< 10 TB',    test: (tb) => tb != null && tb < 10 },
  medium: { label: '10 – 50 TB', test: (tb) => tb != null && tb >= 10 && tb <= 50 },
  large:  { label: '> 50 TB',    test: (tb) => tb != null && tb > 50 },
  none:   { label: 'Sin dato',   test: (tb) => tb == null },
};

function StorageArraysList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const { pathFor } = useLocationPaths(companyId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [search, setSearch] = useState('');
  const [filterFlavor, setFilterFlavor] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEnv, setFilterEnv] = useState('');
  const [filterProtocol, setFilterProtocol] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterRack, setFilterRack] = useState('');
  const [filterCapacity, setFilterCapacity] = useState('');
  const [hlId, setHlId] = useState(null);   // resaltado por ?destacar=<id>

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [selectedItem, setSelectedItem] = useState(null);

  const [componentsItem, setComponentsItem] = useState(null);
  const [portMapItem, setPortMapItem] = useState(null);
  const [portMapElements, setPortMapElements] = useState([]);
  const [disksItem, setDisksItem] = useState(null);
  const [disks, setDisks] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [cloningId, setCloningId] = useState(null);

  /* ── Columnas (catálogo completo; 11 visibles de entrada) ───────────────
   * `render` pinta la celda (píldoras, código, guion de «sin valor») y
   * `sortValue` dice por qué texto/valor se ordena cuando la celda no es texto.
   */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    const protocolsOf = (item) => (Array.isArray(item.protocols) ? item.protocols : []);
    return [
      {
        key: 'name',
        label: tr('storage.name', 'Nombre'),
        sortable: true,
        sortValue: (item) => item.name || '',
        render: (item) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CircleStackIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
            {item.name}
          </span>
        ),
      },
      {
        key: 'hostname',
        label: tr('storage.hostname', 'Hostname'),
        sortable: true,
        render: (item) => item.hostname || dash,
      },
      {
        key: 'ip_address',
        label: tr('storage.ip', 'IP gestión'),
        sortable: true,
        render: (item) => (item.ip_address ? <span className="list-code">{item.ip_address}</span> : dash),
      },
      {
        key: 'storage_flavor',
        label: tr('storage.flavor', 'Tipo'),
        sortable: true,
        sortValue: (item) => item.storage_flavor || '',
        render: (item) => (item.storage_flavor
          ? <span className="list-badge">{FLAVOR_LABELS[item.storage_flavor] || item.storage_flavor}</span>
          : dash),
      },
      {
        key: 'os_distribution',
        label: tr('storage.os_distribution', 'SO / Distro'),
        sortable: true,
        render: (item) => item.os_distribution || dash,
      },
      {
        key: 'protocols',
        label: tr('storage.protocols', 'Protocolos'),
        sortable: true,
        sortValue: (item) => protocolsOf(item).join(','),
        render: (item) => {
          const protos = protocolsOf(item);
          if (!protos.length) return dash;
          return (
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
              {protos.map((p) => <span key={p} className="list-badge">{p}</span>)}
            </span>
          );
        },
      },
      {
        key: 'raw_tb',
        label: tr('storage.raw_tb', 'Capacidad bruta'),
        sortable: true,
        sortValue: (item) => item.raw_tb || 0,
        render: (item) => (item.raw_tb ? `${item.raw_tb} TB` : dash),
      },
      {
        key: 'usable_tb',
        label: tr('storage.usable_tb', 'Capacidad usable'),
        sortable: true,
        sortValue: (item) => item.usable_tb || 0,
        render: (item) => (item.usable_tb ? `${item.usable_tb} TB` : dash),
      },
      {
        key: 'raid_level',
        label: tr('storage.raid_level', 'RAID / Pool'),
        sortable: true,
        render: (item) => item.raid_level || dash,
      },
      {
        key: 'status',
        label: tr('storage.status', 'Estado'),
        sortable: true,
        sortValue: (item) => item.status || '',
        render: (item) => {
          const meta = STATUS_META[(item.status || '').toLowerCase()];
          return meta ? <span className={`list-badge ${meta.badge}`}>{meta.label}</span> : dash;
        },
      },
      {
        key: 'environment',
        label: tr('storage.environment', 'Entorno'),
        sortable: true,
        render: (item) => (item.environment ? <span className="list-badge">{item.environment}</span> : dash),
      },
      {
        key: 'location_name',
        label: tr('storage.location', 'Ubicación'),
        sortable: true,
        sortValue: (item) => pathFor(item.location_id) || item.location_name || '',
        render: (item) => pathFor(item.location_id) || item.location_name || dash,
      },
      {
        key: 'rack_name',
        label: tr('storage.rack', 'Rack'),
        sortable: true,
        sortValue: (item) => item.rack_name || '',
        render: (item) => (item.rack_name ? <span className="list-code">{item.rack_name}</span> : dash),
      },
      {
        key: 'role',
        label: tr('storage.role', 'Rol'),
        sortable: true,
        sortValue: (item) => item.role_display || item.role || '',
        render: (item) => item.role_display || item.role || dash,
      },
      { key: 'owner', label: tr('storage.owner', 'Equipo'), sortable: true, render: (item) => item.owner || dash },
      { key: 'license', label: tr('storage.license', 'Licencia'), sortable: true, render: (item) => item.license || dash },
      {
        key: 'license_status',
        label: tr('storage.license_status', 'Estado licencia'),
        sortable: true,
        sortValue: (item) => item.license_status || '',
        render: (item) => {
          if (!item.license_status) return dash;
          const expired = item.license_status === 'expirada';
          return (
            <span className={`list-badge ${expired ? 'list-badge--off' : 'list-badge--on'}`}>
              {expired ? tr('storage.license_expired', 'Expirada') : tr('storage.license_ok', 'Vigente')}
            </span>
          );
        },
      },
      {
        key: 'license_expires_at',
        label: tr('storage.license_expires_at', 'Vencimiento'),
        sortable: true,
        sortValue: (item) => item.license_expires_at || '',
        render: (item) => (item.license_expires_at ? new Date(item.license_expires_at).toLocaleDateString() : dash),
      },
      {
        key: 'last_check_at',
        label: tr('storage.last_check', 'Último check'),
        sortable: true,
        sortValue: (item) => item.last_check_at || '',
        render: (item) => (item.last_check_at ? new Date(item.last_check_at).toLocaleDateString() : dash),
      },
      { key: 'cpu', label: 'CPU', sortable: true, render: (item) => item.cpu || dash },
      {
        key: 'ram_gb',
        label: 'RAM (GB)',
        sortable: true,
        sortValue: (item) => item.ram_gb || 0,
        render: (item) => (item.ram_gb ? `${item.ram_gb} GB` : dash),
      },
      {
        key: 'ha_mode',
        label: tr('storage.ha_mode', 'Alta disponibilidad'),
        sortable: true,
        sortValue: (item) => (item.ha_mode ? 1 : 0),
        render: (item) => (item.ha_mode ? <span className="list-badge list-badge--on">✅ HA</span> : dash),
      },
      {
        key: 'controller_count',
        label: tr('storage.controllers', 'Controladoras'),
        sortable: true,
        sortValue: (item) => item.controller_count || 0,
        render: (item) => item.controller_count || dash,
      },
      {
        key: 'tags',
        label: tr('storage.tags', 'Tags'),
        sortable: true,
        sortValue: (item) => (Array.isArray(item.tags) ? item.tags.join(',') : ''),
        render: (item) => {
          const tags = Array.isArray(item.tags) ? item.tags : [];
          return tags.length ? tags.join(', ') : dash;
        },
      },
      {
        key: 'agent_status',
        label: tr('storage.agent', 'Agente'),
        sortable: true,
        sortValue: (item) => item.agent_status || '',
        render: (item) => {
          if (!item.has_agent) return dash;
          const online = item.agent_status === 'online';
          return (
            <span className={`list-badge ${online ? 'list-badge--on' : 'list-badge--off'}`}>
              🤖 {item.agent_status || '—'}
            </span>
          );
        },
      },
      {
        key: 'disks_count',
        label: tr('storage.disks', 'Discos'),
        sortable: true,
        sortValue: (item) => item.disks_count || 0,
        render: (item) => (item.disks_count ? `${item.disks_count}` : dash),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, pathFor]);

  /** Columnas visibles de entrada (el resto, en el selector de columnas). */
  const defaultVisible = [
    'name', 'ip_address', 'storage_flavor', 'protocols', 'raw_tb', 'usable_tb',
    'status', 'location_name', 'rack_name', 'role', 'owner',
  ];

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!companyId) {
        setError('Selecciona una empresa para ver las cabinas de almacenamiento.');
        setItems([]);
        setLoading(false);
        return;
      }
      const response = await api.getStorageArrays(companyId);
      const normalized = (response.data || []).map(item => {
        const roleNames = (item.roles || []).map(r => r.code || r.name).filter(Boolean);
        return { ...item, role: item.role || roleNames.join(', '), role_display: roleNames.join(', ') };
      });
      setItems(normalized);
    } catch (err) {
      setError(err.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const buildClonePayload = (item, nameOverride = null) => {
    const tags = Array.isArray(item.tags) ? item.tags
      : typeof item.tags === 'string' ? item.tags.split(',').map(s => s.trim()).filter(Boolean) : [];
    const roleIds = Array.isArray(item.roles) ? item.roles.map(r => r.id).filter(Boolean) : (item.role_ids || []);
    return {
      name: nameOverride || item.name || '',
      manufacturer: item.manufacturer || '',
      model: item.model || '',
      serial_number: '',
      asset_tag: '',
      hostname: item.hostname || '',
      ip_address: '',
      os: item.os || '',
      environment: item.environment || 'prod',
      status: item.status || 'active',
      location_id: item.location_id || null,
      location_name: item.location_name || '',
      rack_id: null,
      rack_name: '',
      position_u: null,
      form_factor: item.form_factor || 'rack',
      size_u: item.size_u || 2,
      role_ids: roleIds,
      owner: item.owner || '',
      license: item.license || '',
      license_status: item.license_status || 'vigente',
      license_expires_at: item.license_expires_at || '',
      cpu: item.cpu || '',
      ram_gb: item.ram_gb || '',
      tags,
      // Storage-specific
      storage_flavor: item.storage_flavor || 'appliance',
      os_distribution: item.os_distribution || '',
      protocols: Array.isArray(item.protocols) ? [...item.protocols] : [],
      raw_tb: item.raw_tb || '',
      usable_tb: item.usable_tb || '',
      disk_count: item.disk_count || '',
      disk_type: item.disk_type || '',
      disk_size_tb: item.disk_size_tb || '',
      raid_level: item.raid_level || '',
      controller_count: item.controller_count || '',
      ha_mode: item.ha_mode || false,
      management_url: item.management_url || '',
      cache_tb: item.cache_tb || '',
      disk_shelf_count: item.disk_shelf_count || '',
    };
  };

  const cloneItem = async (item) => {
    if (!item?.id) return;
    if (!companyId) { setError('Selecciona una empresa.'); return; }
    setCloningId(item.id);
    try {
      setError(null);
      const [storageRes, compRes] = await Promise.all([
        api.getStorageArrayDiskInventory(item.id, companyId).catch(() => null),
        api.getComponents(item.id, companyId).catch(() => null),
      ]);

      let suggestedName = null;
      try {
        const sugg = await api.suggestName({
          type: 'storage',
          companyId,
          locationId: item.location_id || undefined,
          rackId: item.rack_id || undefined,
          roleIds: Array.isArray(item.roles) ? item.roles.map(r => r.id).filter(Boolean) : (item.role_ids || []),
        });
        suggestedName = sugg?.data?.name || sugg?.name || null;
      } catch { /* ignore */ }

      const payload = buildClonePayload(item, suggestedName || 'PENDIENTE-NOMBRE');
      const createResp = await api.createStorageArray(payload, companyId);
      const newId = createResp?.data?.id || createResp?.id;
      if (!newId) throw new Error('No se pudo obtener el ID de la cabina clonada');

      // Clone components
      const components = Array.isArray(compRes?.data) ? compRes.data : [];
      for (const comp of components) {
        const { ports = [], id, element_id, created_at, updated_at, ...rest } = comp;
        const compPayload = { ...rest, serial_number: '', asset_tag: '' };
        const compResp = await api.createComponent(newId, compPayload, companyId);
        const newCompId = compResp?.data?.id || compResp?.id;
        if (!newCompId) continue;
        for (const port of ports) {
          const { id: _pid, created_at: _ca, updated_at: _ua, connection_id, ...pRest } = port;
          await api.createComponentPort(newId, newCompId, { ...pRest, mac_address: '', wwn: '' }, companyId).catch(() => null);
        }
      }

      // Clone disk inventory
      if (storageRes?.data) {
        const meta = { inventory_date: storageRes.data.inventory_date || '', taken_by: storageRes.data.taken_by || '' };
        const data = storageRes.data.data && typeof storageRes.data.data === 'object'
          ? JSON.parse(JSON.stringify(storageRes.data.data)) : {};
        if (Array.isArray(data.physical_disks)) {
          data.physical_disks = data.physical_disks.map(d => ({ ...d, serial: '', slot: '' }));
        }
        await api.updateStorageArrayDiskInventory(newId, { ...meta, data }, companyId);
      }

      await fetchItems();
    } catch (err) {
      setError(err.message || 'Error al copiar cabina');
    } finally {
      setCloningId(null);
    }
  };

  /* ── Apertura de la ficha (lectura por defecto) ───────────────────────── */
  const handleOpen = (item) => { setSelectedItem(item); setFormMode('view'); setShowForm(true); };
  const handleEdit = (item) => { setSelectedItem(item); setFormMode('edit'); setShowForm(true); };
  const handleCreate = () => { setSelectedItem(null); setFormMode('edit'); setShowForm(true); };
  useAutoOpen(items, handleOpen);

  const handleFormClose = () => { setShowForm(false); setSelectedItem(null); fetchItems(); };

  const handleDelete = async (item) => {
    try {
      const response = await api.deleteStorageArray(item.id, companyId);
      if (response && response.success === false) throw new Error(response.message || '');
      await fetchItems();
    } catch (err) {
      setError(err.message || tr('storage.delete_error', 'No se pudo eliminar la cabina'));
    }
  };

  /* ── Discos: inventario de bahías (solo lectura) ──────────────────────── */
  const openDisks = async (item) => {
    setDisksItem(item);
    setDisks(null);
    setDialogError('');
    try {
      const res = await api.getStorageArrayDiskInventory(item.id, companyId);
      const data = res?.data?.data ?? res?.data ?? {};
      setDisks(Array.isArray(data.physical_disks) ? data.physical_disks : []);
    } catch (err) {
      setDialogError(err.message || tr('storage.disks_error', 'No se pudieron cargar los discos'));
      setDisks([]);
    }
  };

  /* ── Mapa de puertos (mismo modal que paneles y switches) ─────────────── */
  const openPortMap = async (item) => {
    setPortMapItem(item);
    if (!item.rack_id) { setPortMapElements([]); return; }
    try {
      const res = await api.getElements(companyId, { rack_id: item.rack_id });
      setPortMapElements(res.data || []);
    } catch {
      setPortMapElements([]);
    }
  };

  /* ── Resaltado con ?destacar=<id>[&abrir=1] (convención del CMDB) ─────── */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !items.length) return undefined;
    const id = Number(raw);
    const target = items.find((i) => i.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { handleOpen(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`storage-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, items, setSearchParams]);

  /* ── Filtros ──────────────────────────────────────────────────────────── */
  const unique = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
  const envOptions = useMemo(() => unique(items.map((i) => i.environment)), [items]);
  const protocolOptions = useMemo(
    () => unique(items.flatMap((i) => (Array.isArray(i.protocols) ? i.protocols : []))),
    [items],
  );
  // El filtro compara por nombre (es lo que trae el listado); la etiqueta
  // muestra la ruta completa para distinguir ubicaciones homónimas.
  const locationOptions = useMemo(() => unique(items.map((i) => i.location_name)).map((name) => {
    const hit = items.find((i) => i.location_name === name);
    return { value: name, label: (hit && pathFor(hit.location_id)) || name };
  }), [items, pathFor]);
  const rackOptions = useMemo(() => unique(items.map((i) => i.rack_name)), [items]);

  const filters = [
    {
      key: 'flavor',
      label: tr('storage.filter_flavor', 'Tipo'),
      allLabel: tr('storage.filter_flavor_all', 'Todos los tipos'),
      value: filterFlavor,
      onChange: setFilterFlavor,
      options: [
        { value: 'appliance', label: tr('storage.flavor_appliance', 'Cabina dedicada') },
        { value: 'server_based', label: tr('storage.flavor_server_based', 'Servidor + Software') },
      ],
    },
    {
      key: 'status',
      label: tr('storage.filter_status', 'Estado'),
      allLabel: tr('storage.filter_status_all', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: [
        { value: 'active', label: tr('storage.filter_active', 'Activas') },
        { value: 'maintenance', label: tr('storage.filter_maintenance', 'En mantenimiento') },
        { value: 'decom', label: tr('storage.filter_decom', 'Decom') },
      ],
    },
    {
      key: 'env',
      label: tr('storage.filter_env', 'Entorno'),
      allLabel: tr('storage.filter_env_all', 'Todos los entornos'),
      value: filterEnv,
      onChange: setFilterEnv,
      options: envOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'protocol',
      label: tr('storage.filter_protocol', 'Protocolo'),
      allLabel: tr('storage.filter_protocol_all', 'Todos los protocolos'),
      value: filterProtocol,
      onChange: setFilterProtocol,
      options: protocolOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'location',
      label: tr('storage.filter_location', 'Ubicación'),
      allLabel: tr('storage.filter_location_all', 'Todas las ubicaciones'),
      value: filterLocation,
      onChange: setFilterLocation,
      options: locationOptions,
    },
    {
      key: 'rack',
      label: tr('storage.filter_rack', 'Rack'),
      allLabel: tr('storage.filter_rack_all', 'Todos los racks'),
      value: filterRack,
      onChange: setFilterRack,
      options: rackOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'capacity',
      label: tr('storage.filter_capacity', 'Capacidad usable'),
      allLabel: tr('storage.filter_capacity_all', 'Cualquier capacidad'),
      value: filterCapacity,
      onChange: setFilterCapacity,
      options: Object.entries(CAPACITY_RANGES).map(([value, meta]) => ({ value, label: meta.label })),
    },
  ];

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const capacityRange = CAPACITY_RANGES[filterCapacity];
    return items.filter((item) => {
      const protocols = Array.isArray(item.protocols) ? item.protocols : [];
      const matchesSearch = !term ||
        (item.name || '').toLowerCase().includes(term) ||
        (item.hostname || '').toLowerCase().includes(term) ||
        (item.ip_address || '').toLowerCase().includes(term) ||
        (item.role_display || item.role || '').toLowerCase().includes(term) ||
        (item.owner || '').toLowerCase().includes(term) ||
        (item.location_name || '').toLowerCase().includes(term) ||
        (item.rack_name || '').toLowerCase().includes(term) ||
        protocols.join(',').toLowerCase().includes(term);
      const matchesFlavor = !filterFlavor || item.storage_flavor === filterFlavor;
      const matchesStatus = !filterStatus || (item.status || '').toLowerCase() === filterStatus;
      const matchesEnv = !filterEnv || item.environment === filterEnv;
      const matchesProtocol = !filterProtocol || protocols.includes(filterProtocol);
      const matchesLocation = !filterLocation || item.location_name === filterLocation;
      const matchesRack = !filterRack || item.rack_name === filterRack;
      const usable = item.usable_tb === '' || item.usable_tb == null ? null : Number(item.usable_tb);
      const matchesCapacity = !capacityRange || capacityRange.test(usable);
      return matchesSearch && matchesFlavor && matchesStatus && matchesEnv
        && matchesProtocol && matchesLocation && matchesRack && matchesCapacity;
    });
  }, [items, search, filterFlavor, filterStatus, filterEnv, filterProtocol, filterLocation, filterRack, filterCapacity]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navItems = useNavOrder(filteredItems, selectedItem, showForm);

  /** Texto plano de cada celda para el Excel (mismas etiquetas que la tabla). */
  function cellText(key, item) {
    if (key === 'storage_flavor') return FLAVOR_LABELS[item.storage_flavor] || item.storage_flavor || '';
    if (key === 'protocols') return (Array.isArray(item.protocols) ? item.protocols : []).join(', ');
    if (key === 'status') return STATUS_META[(item.status || '').toLowerCase()]?.label || '';
    if (key === 'raw_tb' || key === 'usable_tb') {
      return item[key] != null && item[key] !== '' ? `${item[key]} TB` : '';
    }
    if (key === 'ram_gb') return item.ram_gb != null ? `${item.ram_gb} GB` : '';
    if (key === 'ha_mode') return item.ha_mode ? 'Sí' : 'No';
    if (key === 'role') return item.role_display || item.role || '';
    if (key === 'tags') return (item.tags || []).join(', ');
    if (key === 'location_name') return pathFor(item.location_id) || item.location_name || '';
    if (key === 'license_status') {
      if (item.license_status === 'vigente') return 'Vigente';
      if (item.license_status === 'expirada') return 'Expirada';
      return item.license_status || '';
    }
    if (key === 'license_expires_at' || key === 'last_check_at') {
      return item[key] ? new Date(item[key]).toLocaleDateString() : '';
    }
    const value = item[key];
    return value === undefined || value === null ? '' : value;
  }

  // Exporta TODAS las columnas del catálogo (no solo las visibles): es un
  // inventario, y en Excel quitar columnas sobrantes es inmediato.
  const exportRows = useMemo(() => filteredItems.map(item => {
    const row = {};
    columns.forEach((col) => { row[col.label] = cellText(col.key, item); });
    return row;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filteredItems, columns]);

  const subtitle = `${items.length} ${tr('storage.plural', 'cabinas')}`
    + ` · ${filteredItems.length} ${tr('location.filtered', 'filtradas')}`;

  /** Tarjetas: lo esencial de cada cabina, con los mismos botones de fila. */
  function renderStorageCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((item) => {
          const meta = STATUS_META[(item.status || '').toLowerCase()];
          const protos = Array.isArray(item.protocols) ? item.protocols : [];
          const capacity = [
            item.raw_tb ? `${item.raw_tb} TB bruto` : null,
            item.usable_tb ? `${item.usable_tb} TB usable` : null,
          ].filter(Boolean).join(' · ');
          return (
            <div
              key={item.id}
              className="list-card"
              role="button"
              tabIndex={0}
              onClick={() => handleOpen(item)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(item); }}
              title={`${tr('action.view', 'Ver')} ${item.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">💾 {item.name}</div>
                  {item.hostname && item.hostname !== item.name && <span className="list-code">{item.hostname}</span>}
                </div>
                {meta && <span className={`list-badge ${meta.badge}`}>{meta.label}</span>}
              </div>

              <div className="list-card-meta">
                {item.ip_address && <span className="list-code">{item.ip_address}</span>}
                {item.storage_flavor && <span>{FLAVOR_LABELS[item.storage_flavor] || item.storage_flavor}</span>}
                {protos.map((p) => <span key={p} className="list-badge">{p}</span>)}
                {item.ha_mode && <span className="list-badge list-badge--on">✅ HA</span>}
                {capacity && <span>💽 {capacity}</span>}
                {(item.location_name || item.rack_name) && (
                  <span>📍 {[pathFor(item.location_id) || item.location_name, item.rack_name].filter(Boolean).join(' › ')}</span>
                )}
                {item.owner && <span>👤 {item.owner}</span>}
              </div>

              <div className="list-card-actions">{rowActions(item)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const STORAGE_VIEWS = useMemo(() => [
    { key: 'list', label: tr('storage.view_list', 'Lista'), icon: ListBulletIcon, title: tr('storage.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('storage.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('storage.view_cards_hint', 'Tarjetas con lo esencial de cada cabina'), render: renderStorageCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, pathFor]);
  useListView('storage', 'list');

  return (
    <>
      {showForm && selectedItem?.has_agent && (
        <ServerForm
          server={selectedItem}
          companyId={companyId}
          initialMode={formMode}
          onClose={handleFormClose}
          onSave={fetchItems}
          navItems={navItems}
          navIndex={navItems.findIndex(i => i.id === selectedItem.id)}
          onNavigate={(it) => setSelectedItem(it)}
        />
      )}
      {showForm && !selectedItem?.has_agent && (
        <StorageArrayForm
          storage={selectedItem}
          companyId={companyId}
          initialMode={formMode}
          onClose={handleFormClose}
          onSave={(creado) => {
            fetchItems();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setSelectedItem(creado); setFormMode('edit'); }
          }}
          onClone={cloneItem}
          cloningId={cloningId}
          navItems={navItems}
          navIndex={selectedItem ? navItems.findIndex(i => i.id === selectedItem.id) : -1}
          onNavigate={(it) => setSelectedItem(it)}
          variant={getServerFormStyle() === 'cards' ? 'cards' : 'classic'}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={CircleStackIcon}
          title={tr('storage.title', 'Cabinas de almacenamiento')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('storage.new', 'Nueva cabina')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('storage.search_placeholder', 'Buscar por nombre, hostname, IP, rol, protocolo…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="cabinas"
          exportSheetName="Cabinas"

          columns={columns}
          columnStorageKey="cmdb_cols_storage"
          companyId={companyId}
          onImported={() => fetchItems()}
          defaultVisible={defaultVisible}
          rows={filteredItems}
          rowKey={(item) => item.id}
          rowId={(item) => `storage-row-${item.id}`}
          rowClassName={(item) => (item.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={handleOpen}
          rowTitle={(item) => `${tr('action.view', 'Ver')} ${item.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="elements"
                supportsRack
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchItems();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="storage_arrays"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchItems();
                }}
              />
            </>
          )}

          views={STORAGE_VIEWS}
          listKey="storage"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando cabinas de almacenamiento…')}
          error={error}

          empty={{
            icon: '💾',
            title: items.length === 0
              ? tr('storage.empty', 'No hay cabinas de almacenamiento todavía')
              : tr('msg.no_results', 'Ninguna cabina coincide con los filtros'),
            subtitle: items.length === 0 ? tr('storage.empty_hint', 'Crea la primera para empezar a inventariar.') : undefined,
            action: items.length === 0 && companyId ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('storage.new', 'Nueva cabina')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (item) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  disabled={!item.rack_id}
                  onClick={() => item.rack_id && navigate(`/infrastructure/rack/${item.rack_id}`)}
                  title={tr('storage.view_rack', 'Ver rack')}
                >
                  <CubeIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openDisks(item)}
                  title={tr('storage.disks', 'Discos (inventario de bahías)')}
                >
                  <CircleStackIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => setComponentsItem(item)}
                  title={tr('storage.components', 'Componentes (NICs / HBAs)')}
                >
                  <CpuChipIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => openPortMap(item)}
                  title={tr('storage.port_map', 'Mapa de puertos')}
                >
                  <MapIcon />
                </button>
              </>
            ),
            onCopy: cloneItem,
            canCopy: (item) => cloningId !== item.id,
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('storage.delete_title', 'Eliminar cabina'),
            deleteMessage: (item) => (
              <>
                {tr('storage.delete_confirm', '¿Seguro que quieres eliminar la cabina')} <strong>{item.name}</strong>?{' '}
                {tr('storage.delete_hint', 'Se eliminarán también sus componentes, sus discos y los LUNs que publica. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Diálogo: inventario de discos (solo lectura) */}
      {disksItem && (
        <Dialog
          title={`${tr('storage.disks_of', 'Discos de')} ${disksItem.name}`}
          icon="💽"
          maxWidth={760}
          onClose={() => { setDisksItem(null); setDisks(null); setDialogError(''); }}
          footer={(
            <button
              type="button"
              className="list-btn-secondary"
              onClick={() => { setDisksItem(null); setDisks(null); setDialogError(''); }}
            >
              {tr('action.close', 'Cerrar')}
            </button>
          )}
        >
          {!disks && !dialogError && <p className="list-hint">{tr('msg.loading', 'Cargando…')}</p>}
          {dialogError && <p className="list-hint">{dialogError}</p>}
          {disks && (disks.length === 0
            ? <p>{tr('storage.no_disks', 'No hay discos inventariados en esta cabina.')}</p>
            : (
              <table className="list-table">
                <thead>
                  <tr>
                    <th>{tr('storage.disk_slot', 'Bahía')}</th>
                    <th>{tr('storage.disk_model', 'Fabricante / Modelo')}</th>
                    <th>{tr('storage.disk_capacity', 'Capacidad')}</th>
                    <th>{tr('storage.disk_type', 'Tipo')}</th>
                    <th>{tr('storage.disk_serial', 'Nº de serie')}</th>
                  </tr>
                </thead>
                <tbody>
                  {disks.map((disk, idx) => (
                    <tr key={`${disk.slot || idx}`}>
                      <td><span className="list-code">{disk.slot || '—'}</span></td>
                      <td>{[disk.manufacturer, disk.model].filter(Boolean).join(' ') || '—'}</td>
                      <td>{disk.capacity_gb ? `${disk.capacity_gb} GB` : '—'}</td>
                      <td>{[disk.type, disk.interface].filter(Boolean).join(' · ') || '—'}</td>
                      <td>{disk.serial || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
        </Dialog>
      )}

      {/* Diálogo: componentes (NICs / HBAs) */}
      {componentsItem && (
        <ComponentsModal
          element={componentsItem}
          companyId={companyId}
          onClose={() => setComponentsItem(null)}
        />
      )}

      {/* Diálogo: mapa de puertos (mismo modal que paneles y switches) */}
      {portMapItem && (
        <PortMapModal
          element={portMapItem}
          rackElements={portMapElements}
          companyId={companyId}
          onClose={() => { setPortMapItem(null); setPortMapElements([]); }}
          onUpdated={fetchItems}
        />
      )}
    </>
  );
}

export default StorageArraysList;
